import { Loader2, Lock } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useAuth } from "@/context/AuthContext";

export const PageHeader = ({ title, subtitle, children, sheet }) => (
  <div className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between mb-5 fade-up">
    <div>
      {sheet && <div className="font-mono text-[11px] text-emerald-700 mb-1 tracking-wider">{sheet}</div>}
      <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" data-testid="page-title">{title}</h1>
      {subtitle && <p className="text-sm text-slate-600 mt-1 max-w-2xl">{subtitle}</p>}
    </div>
    {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
  </div>
);

export const StatPill = ({ label, value, tone = "slate", testId }) => {
  const tones = {
    slate: "border-slate-300 bg-white",
    green: "border-emerald-300 bg-emerald-50",
    amber: "border-amber-300 bg-amber-50",
    blue: "border-sky-300 bg-sky-50",
  };
  return (
    <div className={`border rounded-sm px-4 py-3 ${tones[tone]}`} data-testid={testId}>
      <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-600">{label}</div>
      <div className="font-mono text-xl font-semibold text-slate-900 mt-1 truncate">{value}</div>
    </div>
  );
};

export const NativeSelect = ({ className = "", children, ...props }) => (
  <select
    className={`h-9 rounded-md border border-input bg-white px-3 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-600 ${className}`}
    {...props}
  >
    {children}
  </select>
);

export const PeriodInput = ({ value, onChange, testId = "period-input" }) => (
  <input
    type="month"
    value={value}
    onChange={(e) => e.target.value && onChange(e.target.value)}
    className="h-9 rounded-md border border-input bg-white px-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-emerald-600"
    data-testid={testId}
  />
);

export const Loading = () => (
  <div className="flex items-center justify-center py-16 text-slate-500" data-testid="loading">
    <Loader2 className="h-5 w-5 animate-spin mr-2" /> Memuat...
  </div>
);

export const Empty = ({ children }) => (
  <div className="border border-dashed border-slate-300 rounded-sm py-10 text-center text-sm text-slate-500 bg-white" data-testid="empty-state">
    {children}
  </div>
);

export const NoAccess = () => (
  <div className="py-20 text-center" data-testid="no-access">
    <Lock className="h-8 w-8 mx-auto text-slate-400" />
    <p className="mt-3 text-slate-600">Anda tidak memiliki hak akses ke menu ini.</p>
  </div>
);

export const Perm = ({ perm, children }) => {
  const { can } = useAuth();
  return can(perm) ? children : <NoAccess />;
};

export const StatusBadge = ({ status }) =>
  status === "cancelled" ? (
    <span className="text-[11px] font-semibold px-2 py-0.5 rounded-sm bg-slate-200 text-slate-600 line-through">DIBATALKAN</span>
  ) : status === "approved" ? (
    <span className="text-[11px] font-semibold px-2 py-0.5 rounded-sm bg-emerald-100 text-emerald-800">DISAHKAN</span>
  ) : (
    <span className="text-[11px] font-semibold px-2 py-0.5 rounded-sm bg-amber-100 text-amber-800">DRAFT</span>
  );

export function Confirm({ trigger, title, description, onConfirm, confirmLabel = "Ya, lanjutkan", testId }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {description && <AlertDialogDescription>{description}</AlertDialogDescription>}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel data-testid={`${testId}-cancel`}>Batal</AlertDialogCancel>
          <AlertDialogAction onClick={onConfirm} data-testid={`${testId}-confirm`}>{confirmLabel}</AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
